import React, { useState, useEffect } from 'react';
import './App.css';
import apiService from './services/api';
import MapComponent from './components/MapComponent';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { locationService } from './services/geolocation';
import Auth from './components/Auth';

const MainContent = () => {
  const { user, logout } = useAuth();
  const [points, setPoints] = useState(0);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPosition, setUserPosition] = useState(null);
  const [gpsError, setGpsError] = useState(false);

  const refreshData = async () => {
    try {
      const pointsData = await apiService.getUserPoints();
      const basesData = await apiService.getUserBases();

      setPoints(pointsData.points);
      setBases(basesData.bases);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const pointsData = await apiService.getUserPoints();
        const basesData = await apiService.getUserBases();

        setPoints(pointsData.points);
        setBases(basesData.bases);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Geolocation setup
  useEffect(() => {
    const getUserPosition = async () => {
      try {
        const position = await locationService.getCurrentLocation();
        setUserPosition(position);
      } catch (error) {
        console.error('Failed to get user position:', error);
        if (error.code === 'PERMISSION_DENIED') {
          setGpsError(true);
        }
      }
    };

    getUserPosition();

    const stream = locationService.getPositionStream({
      interval: 5000,
      accuracy: 'high'
    });

    stream.addEventListener('data', (event) => {
      const { lat, lng, accuracy } = event.detail;
      localStorage.setItem('lastPosition', JSON.stringify({ lat, lng }));
      setUserPosition({ lat, lng, accuracy });
    });

    stream.addEventListener('error', (event) => {
      console.error('Position stream error:', event.detail);
      const code = event.detail.code;
      if (code === 'PERMISSION_DENIED') {
        setGpsError(true);
      }
    });

    return () => {
      locationService.dispose();
    };
  }, []);

  const handleBuild = async (type, position) => {
    try {
      let name = '';
      if (type === 'home') name = 'Home';
      else if (type === 'trading') name = 'Trading Point';

      console.log(`Building ${name} at:`, position);
      await apiService.buildBase(position, name);

      await refreshData();
    } catch (error) {
      console.error('Error building base:', error);
      alert('Failed to build base: ' + error.message);
    }
  };

  const homePoint = bases.find(b => b.name === 'Home');
  const tradingPoint = bases.find(b => b.name === 'Trading Point');

  if (!user) {
    return <Auth />;
  }



  if (loading) {
    return (
      <div className="App">
        <div className="App-loading">
          <div className="loader">
            <div className="spinner"></div>
            <p>Loading your caravan...</p>
          </div>
        </div>
      </div>
    );
  }

  // Get user initials for avatar
  const getInitials = (email) => {
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="App">
      {/* Full-screen map as base layer */}
      <div className="map-wrapper">
        <MapComponent
          points={points}
          bases={bases}
          userPosition={userPosition}
        />
      </div>

      {/* Overlay layer with controls */}
      <div className="overlay-layer">
        {/* Top bar with user info and stats */}
        <div className="top-bar">
          <div className="user-badge">
            <div className="avatar">{getInitials(user.email)}</div>
            <span className="email">{user.email}</span>
            <button className="logout-btn" onClick={logout} title="Logout">
              <span className="material-icons">logout</span>
            </button>
          </div>

          <div className="stats-bar">
            <div className="stat-chip">
              <span className="material-icons">toll</span>
              <span className="value">{points}</span>
              <span className="label">pts</span>
            </div>
            <div className="stat-chip">
              <span className="material-icons">location_city</span>
              <span className="value">{bases.length}</span>
              <span className="label">bases</span>
            </div>
          </div>
        </div>

        {/* Spacer to push bottom controls down */}
        <div className="overlay-spacer"></div>

        {/* Bottom controls panel */}
        <div className="bottom-controls">
          <div className="point-panels">
            {/* Home Point Panel */}
            <div className={`point-panel home ${homePoint ? 'set' : ''}`}>
              <div className="icon-wrapper">
                <span className="material-icons">home</span>
              </div>
              <div className="title">Home</div>
              <div className={`status ${homePoint ? 'set' : 'empty'}`}>
                {homePoint ? 'Established' : 'Not set'}
              </div>
              {!homePoint ? (
                <button
                  className="establish-btn"
                  onClick={() => handleBuild('home', userPosition)}
                  disabled={!userPosition}
                  title={!userPosition ? "Waiting for location..." : "Establish Home at current location"}
                >
                  {!userPosition ? (
                    <>
                      <span className="material-icons spin">gps_not_fixed</span>
                      Locating...
                    </>
                  ) : (
                    <>
                      <span className="material-icons">add_location</span>
                      Create
                    </>
                  )}
                </button>
              ) : (
                <div className="established-badge">
                  <span className="material-icons">check_circle</span>
                  Active
                </div>
              )}
            </div>

            {/* Trading Point Panel */}
            <div className={`point-panel trading ${tradingPoint ? 'set' : ''}`}>
              <div className="icon-wrapper">
                <span className="material-icons">storefront</span>
              </div>
              <div className="title">Trading</div>
              <div className={`status ${tradingPoint ? 'set' : 'empty'}`}>
                {tradingPoint ? 'Established' : 'Not set'}
              </div>
              {!tradingPoint ? (
                <button
                  className="establish-btn"
                  onClick={() => handleBuild('trading', userPosition)}
                  disabled={!userPosition}
                  title={!userPosition ? "Waiting for location..." : "Establish Trading Point at current location"}
                >
                  {!userPosition ? (
                    <>
                      <span className="material-icons spin">gps_not_fixed</span>
                      Locating...
                    </>
                  ) : (
                    <>
                      <span className="material-icons">add_location</span>
                      Create
                    </>
                  )}
                </button>
              ) : (
                <div className="established-badge">
                  <span className="material-icons">check_circle</span>
                  Active
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <MainContent />
    </AuthProvider>
  );
}

export default App;
